<template>
  <div class="profile">
    <div class="profile__loading" v-if="loading">
      <loader />
    </div>
    <div class="profile__user" v-else>
      <div class="profile__image">
        <svg id="icon-user" width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z"
            stroke="#FFF8F2" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="profile__bio">
        <div class="profile__name">
          <div class="profile__save" v-if="editing">
            <text-input id="name" type="text" v-model="displayName" />
            <a @click="saveName">Save</a>
          </div>
          <div v-else>
            <a @click="editName">
              {{displayName || 'Add a name'}}
            </a>
          </div>
        </div>
        <div class="profile__email">{{email}}</div>
        <div class="profile__logout">
          <a @click="logout()">Log out</a>
        </div>
      </div>
      <div class="profile__section">
        <h3>Your Payments</h3>
        <ul v-if="payments && payments.length">
          <li class="profile__payment" v-for="payment in payments" :key="payment.id">
            <span>{{ payment.season }}</span>
            <small>
              Paid <money-string v-bind="payment" /> on {{formatDate(payment.createdAt)}}
            </small>
          </li>
        </ul>
        <p v-else>No payments yet</p>
      </div>
      <div class="profile__section">
        <h3>Your Account</h3>
        <div class="profile__delete" v-if="showDelete">
          <a @click="showDelete = false">Cancel</a>
          <a @click="remove">Confirm Delete</a>
        </div>
        <div v-else>
          <a @click="showDelete = true">Delete account</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import { mapGetters, mapActions } from 'vuex';
import { TEXTDATEFORMAT } from '@/utilities/time';
import MoneyString from '@/components/MoneyString.vue';
import TextInput from '@/components/TextInput.vue';
import Loader from '@/components/Loader.vue';

export default {
  name: 'Profile',
  components: {
    MoneyString,
    TextInput,
    Loader,
  },
  data() {
    return {
      loading: true,
      editing: false,
      email: null,
      displayName: null,
      payments: null,
      showDelete: false,
    };
  },
  computed: {
    ...mapGetters(['getUser']),
  },
  created() {
    if (this.getUser) {
      this.fetchPayments(this.getUser.email).finally(() => {
        this.loading = false;
        this.setUser();
      });
    } else {
      this.$router.push({ name: 'Home' });
    }
  },
  methods: {
    ...mapActions(['fetchPayments', 'updateUser', 'deleteUser', 'signOut']),
    setUser() {
      const { email, displayName, payments } = this.getUser;
      this.email = email;
      this.displayName = displayName;
      this.payments = payments;
    },
    formatDate(date) {
      return dayjs.unix(date.seconds).format(TEXTDATEFORMAT);
    },
    editName() {
      this.editing = true;
    },
    saveName() {
      const { displayName } = this;
      this.updateUser({
        displayName,
      }).then(() => {
        this.editing = false;
      });
    },
    logout() {
      this.loading = true;
      this.signOut();
      this.$router.push({ name: 'Home' });
    },
    remove() {
      this.deleteUser().finally(() => this.logout());
    },
  },
};
</script>

<style lang="scss" scoped>
.profile {
  text-align: center;
  margin: 0 auto;
  padding: 20px;
  max-width: 400px;
  &__user {
    margin-bottom: 30px;
  }
  &__image {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: var(--color-dark);
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 auto 20px;
  }
  &__name {
    font-weight: 500;
    position: relative;
    a {
      margin-top: 10px;
    }
  }
  &__save {
    position: relative;
    margin-bottom: 10px;
    a {
      position: absolute;
      top: 0;
      right: 15px;
    }
  }
  &__logout {
    margin-top: 20px;
    a {
      height: 40px;
      display: inline-flex;
      margin: 5px;
      align-items: center;
      padding: 10px 20px;
      border-radius: 50px;
      border: solid 2px var(--color-dark);
      color: var(--color-dark);
      font-weight: 500;
      text-decoration: none;
      &:hover {
        border-color: var(--color-link);
        color: var(--color-link);
        svg path {
          fill: var(--color-link);
        }
      }
    }
  }
  &__section {
    margin: 50px 0;
    h3 {
      font-weight: 500;
      font-size: var(--font-small);
      text-transform: uppercase;
      color: var(--color-link-inactive);
    }
    li {
      padding: 20px;
      border: solid 1px rgba(0,0,0,0.05);
      border-radius: var(--radius);
      margin: 20px;
      &:last-child {
        margin-bottom: 0;
      }
    }
    p {
      color: var(--color-link-inactive);
    }
  }
  &__payment {
    > span, > small {
      display: block;
    }
    small {
      color: var(--color-link-inactive);
    }
  }
  &__delete {
    a {
      margin: 10px 0;
      display: block;
    }
  }
}
</style>
